<script lang="ts">
    import type {FormControlOptions} from '../../internal/forms/types';
    import type {ValueEditorSize} from '../../types';
    import {getContext, onDestroy} from 'svelte';
    import {EDITORS_LOCALIZE} from '../../context-keys';
    import type {FormControl} from '../../internal/forms/form-control';
    import {FormControlBuilder} from '../../internal/forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import type {ValueEditorOptions} from '../../kp-value-editor/types';
    import type {YearValueEditorLocalizations, YearValueEditorValidations} from './types';
    import {focusOnMount} from '../../internal/use.focus-on-mount';
    import {formControls} from '../../internal/forms/use.form-controls';
    import {touched} from '../../internal/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import {
        validatorFactory,
        yearFromNumberToStringFormatter,
        yearFromStringToNumberParser
    } from './utils';
    import {dateFormatter, dateParser} from '../_shared/date-time-utils';
    import type {EditorsLocalizationFunction} from '../../localizations/types';
    import {ignoreUnsupportedProperties} from '../_shared/editors-utils';
    import {required} from '../../internal/forms/use.required';
    import {getLogger} from '../../../../svelte/context';


    export let model: number;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: ValueEditorOptions;
    export let validations: YearValueEditorValidations;

    ignoreUnsupportedProperties(options);

    const localize: EditorsLocalizationFunction<YearValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([(value: string) => value.trim(), dateParser({customViewFormat: 'y', customModelFormat: 'y'}), yearFromStringToNumberParser()])
        .withFormatters([dateFormatter({customViewFormat: 'y', customModelFormat: 'y'}), yearFromNumberToStringFormatter()])
        .build(getLogger());


    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    })

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    export function getFormController(): FormControl<number> {
        return formControl;
    }
</script>

<template>
    <input class="form-control input-{size}"
           type="text"
           id={editorId}
           name={editorName}
           {placeholder}
           disabled={isDisabled}
           use:required={validations.required}
           use:focusOnMount={isFocused}
           use:formControls={formControl}
           use:touched={{formControl}}
           use:style={{field: formControl.getFieldStateAsStore()}}
           use:ariaInvalid={formControl}
           use:ariaErrormessage={formControl}
           use:ariaDescribedby={inputDescription}

           data-main-input/>

    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>

    <Description {inputDescription}/>

</template>